import { css } from '@emotion/react';
import {
  PhoneHours,
  PhoneHoursScheduleRule,
  ReadPhoneHoursResponse,
} from '@weave/schema-gen-ts/dist/schemas/phone/callroute/v1/callroute_service.pb';
import dayjs from 'dayjs';
import { useTranslation } from '@frontend/i18n';
import { Icon } from '@frontend/icons';
import { theme } from '@frontend/theme';
import { Button, Text, Tray, useModalControl, TextLink, Truncated } from '@frontend/design-system';
import { dayOfWeekMap, groupByDayOfWeek } from '../../utils/dates';
import { OverrideNotification } from './phone-hours-form/override-notification';
import { PhoneHoursTraySubHeader } from './phone-hours-tray-sub-header';

// Helper function to format time from 24-hour format to 12-hour format
const formatTime = (time: string) => {
  // Return time in following format: 1:00pm
  return dayjs(time, 'HH:mm:ss').format('h:mma');
};

export const DayHoursList = (props: { phoneHours: PhoneHoursScheduleRule[]; useLargerFonts?: boolean }) => {
  const dayHours = groupByDayOfWeek(props.phoneHours);
  const orderedDays = [1, 2, 3, 4, 5, 6, 0]; // Monday to Sunday

  return (
    <div
      css={css`
        display: flex;
        flex-direction: column;
        gap: ${theme.spacing(2)};
      `}
    >
      {orderedDays.map((day) => {
        const { openStartTime, openEndTime, breaks } = dayHours[day] || {};
        return (
          <DayHoursListItem
            key={day}
            dayOfWeek={day}
            openStartTime={openStartTime}
            openEndTime={openEndTime}
            breaks={breaks}
            useLargerFonts={props.useLargerFonts}
          />
        );
      })}
    </div>
  );
};

interface PhoneHoursTrayProps {
  modalControls: ReturnType<typeof useModalControl>;
  selectedPhoneHour: ReadPhoneHoursResponse;
  mountTarget: string;
  onEditClick: (phoneHourId: string) => void;
  selectedLocationId: string;
  isUnify: boolean;
  currentPhoneHour: PhoneHours;
  onPreviousClick: () => void;
  onNextClick: () => void;
  isPreviousDisabled: boolean;
  isNextDisabled: boolean;
  isOverrideEnabled: boolean;
}
export const PhoneHoursTray = ({
  modalControls,
  selectedPhoneHour,
  mountTarget,
  onEditClick,
  selectedLocationId,
  currentPhoneHour,
  onPreviousClick,
  onNextClick,
  isPreviousDisabled,
  isNextDisabled,
  isOverrideEnabled,
}: PhoneHoursTrayProps) => {
  const { t } = useTranslation('phone');
  const callRouteName = currentPhoneHour.scheduleCallRoute[0].name;
  const callRouteId = currentPhoneHour.scheduleCallRoute[0].id;

  if (!modalControls) return null;
  return (
    <>
      <Tray
        mountTarget={mountTarget}
        {...modalControls.modalProps}
        width='medium'
        css={{
          gap: theme.spacing(2),
          overflowY: 'auto',
          display: 'flex',
          flexDirection: 'column',
        }}
        showBackdrop={false}
      >
        <Tray.Header
          Buttons={<Button iconName='x' onClick={() => modalControls?.closeModal()} variant='secondary' size='large' />}
        >
          {t('Phone Hours')}
        </Tray.Header>
        <PhoneHoursTraySubHeader
          scheduleCallRoutes={currentPhoneHour?.scheduleCallRoute ?? []}
          selectedLocationId={selectedLocationId}
        />
        <div
          key={selectedPhoneHour.id}
          css={{
            display: 'flex',
            flexDirection: 'column',
            gap: isOverrideEnabled ? theme.spacing(2) : theme.spacing(4),
          }}
        >
          <PhoneHoursHeader
            phoneHourName={selectedPhoneHour.name}
            phoneHourId={selectedPhoneHour.id}
            onEditClick={onEditClick}
          />
          {isOverrideEnabled && <OverrideNotification callRouteName={callRouteName} callRouteId={callRouteId} />}
          <DayHoursList phoneHours={selectedPhoneHour.phoneHours} useLargerFonts={true} />
        </div>
        <div
          css={{
            marginTop: 'auto',
            display: 'flex',
            gap: theme.spacing(2),
            justifyContent: 'flex-end',
          }}
        >
          <Button variant='secondary' size='large' onClick={onPreviousClick} disabled={isPreviousDisabled}>
            {t('Previous')}
          </Button>
          <Button variant='secondary' size='large' onClick={onNextClick} disabled={isNextDisabled}>
            {t('Next')}
          </Button>
        </div>
      </Tray>
    </>
  );
};

export const PhoneHoursHeader = ({
  phoneHourName,
  phoneHourId,
  onEditClick,
}: {
  phoneHourName: string;
  phoneHourId: string;
  onEditClick: (id: string) => void;
}) => {
  const { t } = useTranslation('phone');

  return (
    <div
      css={{
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        gap: theme.spacing(1),
      }}
    >
      <Truncated css={{ fontSize: '20px', fontWeight: '700' }}>{phoneHourName}</Truncated>
      {phoneHourId && (
        <TextLink
          onClick={() => onEditClick(phoneHourId)}
          css={{ display: 'flex', gap: theme.spacing(0.5), width: 'auto', alignItems: 'center' }}
          weight='bold'
        >
          <Icon name='edit' /> {t('Edit')}
        </TextLink>
      )}
    </div>
  );
};

const DayHoursListItem = ({
  dayOfWeek,
  openStartTime,
  openEndTime,
  breaks,
  useLargerFonts,
}: {
  dayOfWeek: number;
  openStartTime: string;
  openEndTime: string;
  breaks?: { startTime: string; endTime: string; name: string }[];
  useLargerFonts?: boolean;
}) => {
  const { t } = useTranslation('phone');

  return (
    <article
      key={dayOfWeek}
      css={css`
        width: 100%;
        display: flex;
        flex-direction: column;
        gap: ${theme.spacing(2)};
        padding: ${theme.spacing(2)};
        border-radius: ${theme.borderRadius.small};
        border: 1px solid ${theme.colors.neutral20};
      `}
    >
      <div
        css={css`
          display: flex;
          justify-content: space-between;
        `}
      >
        <Text weight='bold' size={useLargerFonts ? 'large' : 'medium'}>
          {dayOfWeekMap[dayOfWeek as keyof typeof dayOfWeekMap]}
        </Text>
        {!!openStartTime ? (
          <Text size={useLargerFonts ? 'large' : 'medium'}>
            {formatTime(openStartTime)} - {formatTime(openEndTime)}
          </Text>
        ) : (
          <Text size={useLargerFonts ? 'large' : 'medium'}>{t('Closed')}</Text>
        )}
      </div>

      {!!breaks?.length && (
        <div
          css={css`
            background-color: ${theme.colors.neutral5};
            border-radius: ${theme.borderRadius.small};
            padding: ${theme.spacing(2)};
          `}
        >
          <Text
            size={useLargerFonts ? 'medium' : 'small'}
            css={css`
              color: ${theme.colors.neutral70};
              border-bottom: 1px solid ${theme.colors.neutral20};
              padding-bottom: ${theme.spacing(2)};
            `}
          >
            {t('Breaks')}
          </Text>
          {breaks
            ?.sort((a, b) => dayjs(a.startTime, 'HH:mm:ss').diff(dayjs(b.startTime, 'HH:mm:ss')))
            .map(({ startTime, endTime, name }) => (
              <div
                key={name}
                css={css`
                  display: flex;
                  justify-content: space-between;
                  padding-top: ${theme.spacing(2)};
                `}
              >
                <Text size={useLargerFonts ? 'large' : 'medium'}>{name}</Text>
                <Text size={useLargerFonts ? 'large' : 'medium'}>
                  {formatTime(startTime)} - {formatTime(endTime)}
                </Text>
              </div>
            ))}
        </div>
      )}
    </article>
  );
};
